import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Music, FolderOpen, Edit, Download, RefreshCw } from "lucide-react";
import logo from "@/assets/logo.png";
import EditGymnastModal from "@/components/EditGymnastModal";
import DanceWarmupsAdmin from "@/components/DanceWarmupsAdmin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [gymnasts, setGymnasts] = useState([]);
  const [spotifyPlaylists, setSpotifyPlaylists] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGymnastModal, setShowGymnastModal] = useState(false);
  const [showSpotifyModal, setShowSpotifyModal] = useState(false);
  const [showPINSettings, setShowPINSettings] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newGymnast, setNewGymnast] = useState({ name: "", categoryId: "", file: null });
  const [newSpotifyPlaylist, setNewSpotifyPlaylist] = useState({ name: "", uri: "", explicit: false });
  const [explicitPIN, setExplicitPIN] = useState("");
  const [newPIN, setNewPIN] = useState("");
  const [defaultBackgroundVolume, setDefaultBackgroundVolume] = useState(20);
  const [defaultFloorVolume, setDefaultFloorVolume] = useState(35);
  const [loading, setLoading] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [spotifyConnected, setSpotifyConnected] = useState(false);
  const [spotifyAccountLoading, setSpotifyAccountLoading] = useState(true);
  const [editingGymnast, setEditingGymnast] = useState(null);
  const [activeTab, setActiveTab] = useState('categories'); // categories, gymnasts, spotify, warmups, settings

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, []);

  const getAuthHeader = () => {
    return { Authorization: `Bearer ${localStorage.getItem("adminToken")}` };
  };

  const fetchData = async () => {
    try {
      const [categoriesRes, gymnastsRes, playlistsRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/gymnasts`),
        axios.get(`${API}/spotify/playlists`)
      ]);
      setCategories(categoriesRes.data);
      setGymnasts(gymnastsRes.data);
      setSpotifyPlaylists(playlistsRes.data);
      
      // Fetch PIN and volume settings separately (requires auth)
      try {
        const settingsRes = await axios.get(`${API}/settings/explicit-pin`, { headers: getAuthHeader() });
        setExplicitPIN(settingsRes.data.pin);
        setDefaultBackgroundVolume(settingsRes.data.default_background_volume || 20);
        setDefaultFloorVolume(settingsRes.data.default_floor_volume || 35);
      } catch (settingsError) {
        console.error("Error fetching settings:", settingsError);
        setExplicitPIN("1234");
        setDefaultBackgroundVolume(20);
        setDefaultFloorVolume(35);
      }

      // Check Spotify connection status
      try {
        const spotifyRes = await axios.get(`${API}/spotify/auth/status`);
        setSpotifyConnected(spotifyRes.data.connected);
      } catch (e) {
        setSpotifyConnected(false);
      } finally {
        setSpotifyAccountLoading(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    }
  };

  const updateExplicitPIN = async () => {
    if (!newPIN || newPIN.length !== 4 || !/^\d+$/.test(newPIN)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/settings/explicit-pin`,
        { pin: newPIN },
        { headers: getAuthHeader() }
      );
      toast.success("PIN updated successfully!");
      setExplicitPIN(newPIN);
      setNewPIN("");
      setShowPINSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update PIN");
    } finally {
      setLoading(false);
    }
  };

  const updateVolumeSettings = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${API}/settings/volumes`,
        { 
          default_background_volume: defaultBackgroundVolume,
          default_floor_volume: defaultFloorVolume
        },
        { headers: getAuthHeader() }
      );
      toast.success("Volume settings updated!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update volume settings");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminEmail");
    navigate("/admin");
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/categories`,
        { name: newCategoryName },
        { headers: getAuthHeader() }
      );
      toast.success("Category created!");
      setNewCategoryName("");
      setShowCategoryModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create category");
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category and all its gymnasts?")) return;

    try {
      await axios.delete(`${API}/categories/${id}`, { headers: getAuthHeader() });
      toast.success("Category deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const createGymnast = async () => {
    if (!newGymnast.name.trim() || !newGymnast.categoryId) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("name", newGymnast.name);
      formData.append("categoryId", newGymnast.categoryId);
      if (newGymnast.file) {
        formData.append("mp3File", newGymnast.file);
      }

      await axios.post(`${API}/gymnasts`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Gymnast added!");
      setNewGymnast({ name: "", categoryId: "", file: null });
      setShowGymnastModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add gymnast");
    } finally {
      setLoading(false);
    }
  };

  const deleteGymnast = async (id) => {
    if (!window.confirm("Delete this gymnast?")) return;

    try {
      await axios.delete(`${API}/gymnasts/${id}`, { headers: getAuthHeader() });
      toast.success("Gymnast deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete gymnast");
    }
  };

  const downloadAllAudio = async () => {
    setDownloadingAll(true);
    toast.info('Building ZIP — this may take a moment...');
    try {
      const response = await fetch(`${API}/gymnasts/download-all`, {
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'FloorMusic.zip';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('All floor music downloaded!');
    } catch (error) {
      toast.error('Failed to download all audio');
    } finally {
      setDownloadingAll(false);
    }
  };

  const downloadGymnastAudio = async (gymnast) => {    try {
      toast.info(`Downloading ${gymnast.name}...`);
      const response = await fetch(`${API}/gymnasts/${gymnast.id}/audio`, {
        headers: getAuthHeader()
      });
      if (!response.ok) throw new Error('Audio not found');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = gymnast.name.replace(/\s+/g, '&') + '.mp3';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Downloaded ${gymnast.name.replace(/\s+/g, '&')}.mp3`);
    } catch (error) {
      toast.error('Failed to download audio');
    }
  };

  const disconnectSpotify = async () => {
    if (!window.confirm("Disconnect the current Spotify account? You will need to reconnect with the new account afterwards.")) return;
    try {
      await axios.post(`${API}/spotify/auth/disconnect`, null, { headers: getAuthHeader() });
      setSpotifyConnected(false);
      toast.success("Spotify account disconnected. You can now connect the new account.");
    } catch (error) {
      toast.error("Failed to disconnect Spotify");
    }
  };

  const connectSpotify = async () => {
    try {
      const callbackUrl = `${window.location.origin}/spotify-callback`;
      const response = await axios.get(`${API}/spotify/auth/login`, {
        params: { redirect_uri: callbackUrl }
      });
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error("Failed to start Spotify connection");
    }
  };

  const createSpotifyPlaylist = async () => {
    if (!newSpotifyPlaylist.name.trim() || !newSpotifyPlaylist.uri.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/spotify/playlists`,
        { 
          name: newSpotifyPlaylist.name, 
          playlistUri: newSpotifyPlaylist.uri,
          explicit: newSpotifyPlaylist.explicit
        },
        { headers: getAuthHeader() }
      );
      toast.success("Spotify playlist added!");
      setNewSpotifyPlaylist({ name: "", uri: "", explicit: false });
      setShowSpotifyModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add playlist");
    } finally {
      setLoading(false);
    }
  };

  const deleteSpotifyPlaylist = async (id) => {
    if (!window.confirm("Delete this playlist?")) return;

    try {
      await axios.delete(`${API}/spotify/playlists/${id}`, { headers: getAuthHeader() });
      toast.success("Playlist deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete playlist");
    }
  };

  return (
    <div className="min-h-screen p-6" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center gap-4">
          <img src={logo} alt="Dynamo Gymnastics" style={{ width: '120px' }} data-testid="dashboard-logo" />
          <h1 className="text-3xl font-bold" data-testid="dashboard-title">Admin Dashboard</h1>
        </div>
        <button onClick={logout} className="btn-secondary flex items-center gap-2" data-testid="logout-button">
          <LogOut size={20} />
          Logout
        </button>
      </div>

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Categories Section */}
        <div className="glass-card" data-testid="categories-section">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2" data-testid="categories-title">
              <FolderOpen size={24} />
              Categories
            </h2>
            <button
              onClick={() => setShowCategoryModal(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="add-category-button"
            >
              <Plus size={20} />
              Add Category
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center justify-between bg-white/5 p-4 rounded-lg" data-testid={`category-item-${cat.id}`}>
                <span className="font-medium" data-testid={`category-name-${cat.id}`}>{cat.name}</span>
                <button
                  onClick={() => deleteCategory(cat.id)}
                  className="text-red-400 hover:text-red-300"
                  data-testid={`delete-category-${cat.id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Gymnasts Section */}
        <div className="glass-card" data-testid="gymnasts-section">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold flex items-center gap-2" data-testid="gymnasts-title">
              <Music size={24} />
              Gymnasts & Floor Music
            </h2>
            <div className="flex gap-2">
              <button
                onClick={downloadAllAudio}
                disabled={downloadingAll}
                className="btn-secondary flex items-center gap-2 disabled:opacity-50"
                data-testid="download-all-button"
                title="Download all floor music as ZIP"
              >
                <Download size={20} />
                {downloadingAll ? 'Preparing...' : 'Download All'}
              </button>
              <button
                onClick={() => setShowGymnastModal(true)}
                className="btn-secondary flex items-center gap-2"
                data-testid="add-gymnast-button"
              >
                <Plus size={20} />
                Add Gymnast
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {gymnasts.map((gymnast) => {
              const cat = categories.find(c => c.id === gymnast.categoryId);
              return (
                <div key={gymnast.id} className="flex items-center justify-between bg-white/5 p-4 rounded-lg" data-testid={`gymnast-item-${gymnast.id}`}>
                  <div>
                    <span className="font-medium block" data-testid={`gymnast-name-${gymnast.id}`}>{gymnast.name}</span>
                    <span className="text-sm opacity-70" data-testid={`gymnast-category-${gymnast.id}`}>{cat?.name || 'Unknown'}</span>
                    <span className="text-xs opacity-60 block" data-testid={`gymnast-audio-status-${gymnast.id}`}>
                      {gymnast.hasAudio ? '🎵 Has audio' : '⚠️ No audio'} • Volume: {gymnast.volume}%
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingGymnast(gymnast)}
                      className="bg-blue-500 hover:bg-blue-600 p-2 rounded-lg transition-colors"
                      data-testid={`edit-gymnast-${gymnast.id}`}
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    {gymnast.hasAudio && (
                      <button
                        onClick={() => downloadGymnastAudio(gymnast)}
                        className="bg-green-600 hover:bg-green-500 p-2 rounded-lg transition-colors"
                        data-testid={`download-gymnast-${gymnast.id}`}
                        title="Download MP3"
                      >
                        <Download size={18} />
                      </button>
                    )}
                    <button
                      onClick={() => deleteGymnast(gymnast.id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-gymnast-${gymnast.id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Spotify Account Management */}
        <div className="glass-card" data-testid="spotify-account-section">
          <h2 className="text-2xl font-bold mb-4">Spotify Account</h2>

          {spotifyAccountLoading ? (
            <p className="text-white/60 text-sm">Checking connection...</p>
          ) : spotifyConnected ? (
            <div className="flex items-center justify-between bg-white/5 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-green-400 inline-block"></span>
                <div>
                  <p className="font-semibold">Account connected</p>
                  <p className="text-sm text-white/60">Spotify is linked and ready to play</p>
                </div>
              </div>
              <button
                onClick={disconnectSpotify}
                className="btn-secondary flex items-center gap-2 text-red-400 border-red-400/40 hover:bg-red-400/10"
                data-testid="disconnect-spotify-button"
              >
                <RefreshCw size={16} />
                Switch Account
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-white/5 p-4 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span>
                <div>
                  <p className="font-semibold">No account connected</p>
                  <p className="text-sm text-white/60">Connect a Spotify account to enable music playback</p>
                </div>
              </div>
              <button
                onClick={connectSpotify}
                className="btn-primary flex items-center gap-2"
                data-testid="connect-spotify-admin-button"
              >
                <Music size={16} />
                Connect Spotify
              </button>
            </div>
          )}
        </div>

        {/* Spotify Playlists Section */}
        <div className="glass-card" data-testid="spotify-section">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold" data-testid="spotify-title">Spotify Playlists</h2>
            <button
              onClick={() => setShowSpotifyModal(true)}
              className="btn-secondary flex items-center gap-2"
              data-testid="add-playlist-button"
            >
              <Plus size={20} />
              Add Playlist
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {spotifyPlaylists.map((playlist) => (
              <div key={playlist.id} className="flex items-center justify-between bg-white/5 p-4 rounded-lg" data-testid={`playlist-item-${playlist.id}`}>
                <span className="font-medium" data-testid={`playlist-name-${playlist.id}`}>{playlist.name}</span>
                <button
                  onClick={() => deleteSpotifyPlaylist(playlist.id)}
                  className="text-red-400 hover:text-red-300"
                  data-testid={`delete-playlist-${playlist.id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Dance Warmups Section */}
        <div className="glass-card">
          <DanceWarmupsAdmin />
        </div>

        {/* Explicit Content PIN Settings */}
        <div className="glass-card" data-testid="pin-settings-section">
          <h2 className="text-2xl font-bold mb-4">4-Digit PIN</h2>
          <p className="text-white/70 mb-4">
            Set a 4-digit PIN used for:
          </p>
          <ul className="list-disc list-inside text-white/70 mb-4 space-y-1">
            <li>Playing playlists marked as explicit</li>
            <li>Refreshing the page (after clicking refresh 3 times)</li>
          </ul>
          
          <div className="bg-white/5 p-4 rounded-lg mb-4">
            <p className="text-sm text-white/50 mb-2">Current PIN:</p>
            <p className="text-2xl font-bold tracking-widest">{explicitPIN ? '•'.repeat(4) : '••••'}</p>
          </div>

          {!showPINSettings ? (
            <button
              onClick={() => setShowPINSettings(true)}
              className="btn-primary"
              data-testid="change-pin-button"
            >
              Change PIN
            </button>
          ) : (
            <div className="space-y-4">
              <input
                type="text"
                value={newPIN}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setNewPIN(value);
                }}
                placeholder="Enter new 4-digit PIN"
                maxLength={4}
                className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white text-center text-2xl tracking-widest"
                data-testid="new-pin-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={updateExplicitPIN}
                  disabled={loading || newPIN.length !== 4}
                  className="btn-primary flex-1 disabled:opacity-50"
                  data-testid="save-pin-button"
                >
                  {loading ? 'Updating...' : 'Update PIN'}
                </button>
                <button
                  onClick={() => {
                    setShowPINSettings(false);
                    setNewPIN('');
                  }}
                  className="btn-secondary flex-1"
                  data-testid="cancel-pin-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Default Volume Settings */}
        <div className="glass-card" data-testid="volume-settings-section">
          <h2 className="text-2xl font-bold mb-4">Default Volume Settings</h2>
          <p className="text-white/70 mb-4">
            Set default volume levels for different types of audio playback.
          </p>
          
          <div className="space-y-6">
            {/* Background Music Volume */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Background Music Volume (Spotify / Radio)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={defaultBackgroundVolume}
                  onChange={(e) => setDefaultBackgroundVolume(parseInt(e.target.value))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #ff3131 0%, #ff3131 ${defaultBackgroundVolume}%, rgba(255, 255, 255, 0.2) ${defaultBackgroundVolume}%, rgba(255, 255, 255, 0.2) 100%)`
                  }}
                  data-testid="background-volume-slider"
                />
                <span className="text-xl font-bold min-w-[4rem] text-center" data-testid="background-volume-value">
                  {defaultBackgroundVolume}%
                </span>
              </div>
              <p className="text-xs text-white/50 mt-1">Recommended: 20%</p>
            </div>

            {/* Floor Music Volume */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Floor Music Volume (MP3 Gymnast Tracks)
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={defaultFloorVolume}
                  onChange={(e) => setDefaultFloorVolume(parseInt(e.target.value))}
                  className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #ff3131 0%, #ff3131 ${defaultFloorVolume}%, rgba(255, 255, 255, 0.2) ${defaultFloorVolume}%, rgba(255, 255, 255, 0.2) 100%)`
                  }}
                  data-testid="floor-volume-slider"
                />
                <span className="text-xl font-bold min-w-[4rem] text-center" data-testid="floor-volume-value">
                  {defaultFloorVolume}%
                </span>
              </div>
              <p className="text-xs text-white/50 mt-1">Recommended: 35%</p>
            </div>

            <button
              onClick={updateVolumeSettings}
              disabled={loading}
              className="btn-primary w-full"
              data-testid="save-volume-settings-button"
            >
              {loading ? 'Saving...' : 'Save Volume Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* Edit Gymnast Modal */}
      {editingGymnast && (
        <EditGymnastModal
          gymnast={editingGymnast}
          categories={categories}
          onClose={() => setEditingGymnast(null)}
          onUpdate={fetchData}
        />
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="category-modal">
          <div className="glass-card max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4" data-testid="category-modal-title">Add Category</h3>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Category Name"
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white mb-4"
              data-testid="category-name-input"
            />
            <div className="flex gap-2">
              <button onClick={createCategory} disabled={loading} className="btn-primary flex-1" data-testid="create-category-button">
                {loading ? "Creating..." : "Create"}
              </button>
              <button onClick={() => setShowCategoryModal(false)} className="btn-secondary flex-1" data-testid="cancel-category-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gymnast Modal */}
      {showGymnastModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="gymnast-modal">
          <div className="glass-card max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4" data-testid="gymnast-modal-title">Add Gymnast</h3>
            <input
              type="text"
              value={newGymnast.name}
              onChange={(e) => setNewGymnast({ ...newGymnast, name: e.target.value })}
              placeholder="Gymnast Name"
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white mb-4"
              data-testid="gymnast-name-input"
            />
            <select
              value={newGymnast.categoryId}
              onChange={(e) => setNewGymnast({ ...newGymnast, categoryId: e.target.value })}
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white mb-4"
              data-testid="gymnast-category-select"
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            <input
              type="file"
              accept="audio/mpeg,audio/mp3"
              onChange={(e) => setNewGymnast({ ...newGymnast, file: e.target.files[0] })}
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white mb-4"
              data-testid="gymnast-file-input"
            />
            <div className="flex gap-2">
              <button onClick={createGymnast} disabled={loading} className="btn-primary flex-1" data-testid="create-gymnast-button">
                {loading ? "Creating..." : "Create"}
              </button>
              <button onClick={() => setShowGymnastModal(false)} className="btn-secondary flex-1" data-testid="cancel-gymnast-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Spotify Modal */}
      {showSpotifyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="spotify-modal">
          <div className="glass-card max-w-md w-full">
            <h3 className="text-2xl font-bold mb-4" data-testid="spotify-modal-title">Add Spotify Playlist</h3>
            <input
              type="text"
              value={newSpotifyPlaylist.name}
              onChange={(e) => setNewSpotifyPlaylist({ ...newSpotifyPlaylist, name: e.target.value })}
              placeholder="Playlist Name"
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white mb-4"
              data-testid="playlist-name-input"
            />
            <input
              type="text"
              value={newSpotifyPlaylist.uri}
              onChange={(e) => setNewSpotifyPlaylist({ ...newSpotifyPlaylist, uri: e.target.value })}
              placeholder="spotify:playlist:xxxxx"
              className="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/30 text-white mb-4"
              data-testid="playlist-uri-input"
            />
            <p className="text-sm opacity-70 mb-2" data-testid="playlist-uri-help">Find playlist URI: Right-click playlist → Share → Copy Spotify URI</p>
            
            {/* Explicit Content Checkbox */}
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={newSpotifyPlaylist.explicit}
                onChange={(e) => setNewSpotifyPlaylist({ ...newSpotifyPlaylist, explicit: e.target.checked })}
                className="w-5 h-5 rounded cursor-pointer"
                data-testid="playlist-explicit-checkbox"
              />
              <span className="text-sm">Mark as Explicit (requires PIN to play)</span>
            </label>

            <div className="flex gap-2">
              <button onClick={createSpotifyPlaylist} disabled={loading} className="btn-primary flex-1" data-testid="create-playlist-button">
                {loading ? "Creating..." : "Create"}
              </button>
              <button onClick={() => setShowSpotifyModal(false)} className="btn-secondary flex-1" data-testid="cancel-playlist-button">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
